import { http } from '@inertiajs/react';
import type { AxiosError } from 'axios';

import {
  GLOBAL_VALIDATION_ERROR_EVENT,
  type GlobalValidationErrorDetail
} from '@/constants/globalValidationError';
import { MODAL_VALIDATION_ERROR_KEYS } from '@/constants/validationErrorKeys';

import type { ValidationErrorResponseBody } from './deletionConstraintErrorDispatch';
import { parseJson } from './parseJson';

const VALIDATION_STATUS = 422;

/**
 * The message a globally handled validation error carries, or `null` when the bag holds none. Only
 * the message is ever read — the key that carried it is an internal contract (`lifecycle_hook`)
 * and must never reach the user.
 */
export function globalValidationErrorMessage(
  errors: Record<string, string | string[]> | undefined
): string | null {
  for (const key of MODAL_VALIDATION_ERROR_KEYS) {
    const raw = errors?.[key];
    const message = Array.isArray(raw) ? raw[0] : raw;

    if (typeof message === 'string' && message !== '') {
      return message;
    }
  }

  return null;
}

/**
 * Dispatches {@link GLOBAL_VALIDATION_ERROR_EVENT} when `body` is a validation response carrying
 * one of the globally handled keys. Those keys name no form field, so without this the rejection
 * reaches no input and the submission looks like it silently did nothing. Returns whether the
 * event was dispatched.
 */
export function dispatchGlobalValidationError(status: number | undefined, body: unknown): boolean {
  if (status !== VALIDATION_STATUS) {
    return false;
  }

  const message = globalValidationErrorMessage(errorBag(body));

  if (message === null) {
    return false;
  }

  window.dispatchEvent(
    new CustomEvent<GlobalValidationErrorDetail>(GLOBAL_VALIDATION_ERROR_EVENT, {
      detail: { message }
    })
  );

  return true;
}

/** Rejection handler for an axios response interceptor. */
export function handleGlobalValidationAxiosError(
  error: AxiosError<ValidationErrorResponseBody>
): void {
  dispatchGlobalValidationError(error.response?.status, error.response?.data);
}

/**
 * Inertia's `useHttp` uses its own HTTP client rather than axios, and its failures are not router
 * visits, so neither the axios interceptor nor `page.props.errors` ever sees them. `http.onError`
 * is the one hook that fires for every Inertia HTTP client request.
 */
export function registerGlobalValidationErrorHandler(): () => void {
  return http.onError((error) => {
    const response = (error as { response?: { status: number; data: unknown } }).response;

    if (response === undefined) {
      return;
    }

    dispatchGlobalValidationError(response.status, response.data);
  });
}

function errorBag(body: unknown): Record<string, string | string[]> | undefined {
  const parsed = typeof body === 'string' ? parseJson(body) : body;

  if (parsed === null || typeof parsed !== 'object') {
    return undefined;
  }

  return (parsed as ValidationErrorResponseBody).errors;
}
